import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { CheckCircle2, ArrowRight } from 'lucide-react';

const Hero = ({ onCalculateClick, onSetupClick }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const videoRefs = useRef([]);
  
  const videos = [
    '/1 (online-video-cutter.com).mp4',
    '/2 (online-video-cutter.com).mp4',
    '/5210844-uhd_3840_2160_30fps (1) (online-video-cutter.com).mp4'
  ];

  useEffect(() => {
    const currentVideo = videoRefs.current[currentVideoIndex];
    
    if (currentVideo) {
      // Load video only when it becomes active
      if (currentVideo.readyState === 0) {
        currentVideo.load();
      }
      
      currentVideo.play().catch(error => {
        console.error('Error playing video:', error);
      });
    }

    const handleVideoEnd = () => {
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
    };

    if (currentVideo) {
      currentVideo.addEventListener('ended', handleVideoEnd);
    }

    return () => {
      if (currentVideo) {
        currentVideo.removeEventListener('ended', handleVideoEnd);
      }
    };
  }, [currentVideoIndex, videos.length]);

  // Pause all videos except the current one
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index !== currentVideoIndex) {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [currentVideoIndex]);

  return (
    <section className="relative pt-32 pb-20 px-4 overflow-hidden bg-white">
      {/* Video Background */}
      <div className="absolute inset-0">
        {videos.map((videoSrc, index) => (
          <video
            key={index}
            ref={(el) => (videoRefs.current[index] = el)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentVideoIndex ? 'opacity-100 z-0' : 'opacity-0 z-[-1]'
            }`}
            muted
            playsInline
            preload={index === 0 ? "metadata" : "none"}
            loading="lazy"
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        ))}
        {/* Subtle dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40 z-[1]"></div>
        <div className="absolute inset-0 opacity-20 z-[1]">
          <div className="absolute top-0 left-0 w-96 h-96 bg-neo-emerald rounded-full mix-blend-screen filter blur-3xl animate-blob opacity-30"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-neo-emerald rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-2000 opacity-20"></div>
          <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-neo-emerald rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-4000 opacity-25"></div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center space-y-8">
          <h1 className="text-headline md:text-[72px] font-heading font-bold text-white leading-tight drop-shadow-lg">
            Turning Agriculture Into <br />
            <span className="text-neo-emerald drop-shadow-lg">Climate Solutions</span>
          </h1>
          
          {/* <p className="text-2xl text-white font-semibold font-body drop-shadow-md">
            मिट्टी को दे ताकत अपार, हर किसान अपनाए बायोचार!
          </p> */}
          
          <p className="text-lg text-white/90 max-w-3xl mx-auto font-body drop-shadow-md">
            Transform agricultural waste into premium biochar, earn carbon credits, and improve soil health with our revolutionary 3-minute setup process.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={(e) => e.preventDefault()}
              className="bg-neo-emerald hover:bg-[#1db868] text-neo-white px-8 py-6 text-lg h-auto group glow-emerald-hover transition-all duration-300 cursor-pointer"
            >
              Start 3-Minute Setup
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              onClick={onCalculateClick}
              className="bg-white/95 hover:bg-white text-neo-emerald border-2 border-white hover:border-white font-semibold px-8 py-6 text-lg h-auto transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Calculate Your Potential
            </Button>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-4">
            <div className="flex items-center gap-2 text-white/90">
              <CheckCircle2 className="w-5 h-5 text-neo-emerald" />
              <span className="font-body drop-shadow-md">No setup fees</span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <CheckCircle2 className="w-5 h-5 text-neo-emerald" />
              <span className="font-body drop-shadow-md">Instant GPS mapping</span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <CheckCircle2 className="w-5 h-5 text-neo-emerald" />
              <span className="font-body drop-shadow-md">Government certified</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
